import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { Button } from "@mui/material";
import type { SocialRelationshipDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.PublicProfile;

type Props = {
  relationship: SocialRelationshipDto | undefined;
  /** The viewer may start following (useSocialPermissions); existing edges render regardless. */
  canFollow: boolean;
  /** Signed in, but the relationship is still loading — the Follow button waits, disabled. */
  isLoading: boolean;
  /** Anonymous visitor: the Follow button is a disabled placeholder. */
  isAnonymous: boolean;
  /** Any graph mutation of this profile is in flight. */
  isMutating: boolean;
  /** Wraps an action with the HandleGate (useHandleGate.guarded). */
  guarded: (action: () => void) => () => void;
  onFollow: () => void;
  onUnfollow: () => void;
  onApprove: () => void;
  onDeleteRequest: () => void;
};

/**
 * The follow side of the public profile: one button for the viewer's own edge (Follow / Follow
 * back / Requested / Following / Mutual) and, when the target asked to follow the viewer, the
 * Approve / Delete pair. An existing edge keeps its control either way (Following, Requested),
 * so it stays reachable after the target goes private or the rule tightened.
 */
export const ProfileFollowButtons = ({
  relationship,
  canFollow,
  isLoading,
  isAnonymous,
  isMutating,
  guarded,
  onFollow,
  onUnfollow,
  onApprove,
  onDeleteRequest,
}: Props) => {
  const { t } = useResource();

  const isFollowVisible =
    relationship?.isFollowed || relationship?.hasOutgoingRequest || canFollow;

  const renderFollowButton = () => {
    if (relationship?.isFollowed) {
      return (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<CheckIcon />}
          disabled={isMutating}
          onClick={onUnfollow}
        >
          {t(relationship.isMutual ? RESOURCES.Mutual : RESOURCES.Following)}
        </Button>
      );
    }

    if (relationship?.hasOutgoingRequest) {
      // Unfollowing withdraws a pending request.
      return (
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={isMutating}
          onClick={onUnfollow}
        >
          {t(RESOURCES.Requested)}
        </Button>
      );
    }

    return (
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<AddIcon />}
        disabled={isAnonymous || isLoading || isMutating}
        onClick={guarded(onFollow)}
      >
        {t(
          relationship?.isFollowedBy ? RESOURCES.FollowBack : RESOURCES.Follow,
        )}
      </Button>
    );
  };

  return (
    <>
      {isFollowVisible && renderFollowButton()}
      {relationship?.hasIncomingRequest && (
        <>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<PersonAddAlt1OutlinedIcon />}
            disabled={isMutating}
            onClick={guarded(onApprove)}
          >
            {t(RESOURCES.ApproveRequest)}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={isMutating}
            onClick={guarded(onDeleteRequest)}
          >
            {t(RESOURCES.DeleteRequest)}
          </Button>
        </>
      )}
    </>
  );
};
